/**
 * Results grid component for displaying query results.
 * Uses ag-Grid for performance and features.
 * Inspired by DBeaver's Data Editor.
 */

import React, { useState, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, CellDoubleClickedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Box, Button, Typography, Paper, Stack, useTheme } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { QueryResult, CellPopupData } from '../types';
import CellPopup from './CellPopup';
import { exportApi } from '../services/api';

interface ResultsGridProps {
  result: QueryResult | null;
  loading?: boolean;
}

const ResultsGrid: React.FC<ResultsGridProps> = ({ result, loading }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [cellPopupData, setCellPopupData] = useState<CellPopupData | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  // Define column definitions from query result
  const columnDefs = useMemo<ColDef[]>(() => {
    if (!result?.columns) return [];

    return result.columns.map((col) => ({
      field: col.name,
      headerName: col.name,
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      valueFormatter: (params: any) => {
        if (params.value === null) return 'NULL';
        if (params.value === undefined) return '';
        if (typeof params.value === 'object') return JSON.stringify(params.value);
        return String(params.value);
      },
    }));
  }, [result?.columns]);

  // Handle cell double-click
  const onCellDoubleClicked = (event: CellDoubleClickedEvent) => {
    const data: CellPopupData = {
      value: event.value,
      columnName: event.column.getColId(),
      rowIndex: event.rowIndex || 0,
    };
    setCellPopupData(data);
    setPopupOpen(true);
  };

  const handleCloseCellPopup = () => {
    setPopupOpen(false);
  };

  const handleExportCsv = async () => {
    if (!result?.rows || !result?.columns) return;

    try {
      const columns = result.columns.map(col => col.name);
      const csv = await exportApi.toCsv(columns, result.rows);
      
      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `query_results_${Date.now()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleExportJson = async () => {
    if (!result?.rows) return;

    try {
      const json = await exportApi.toJson(result.rows);
      
      // Download JSON
      const blob = new Blob([json], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `query_results_${Date.now()}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  if (!result) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Execute a query to see results here
        </Typography>
      </Box>
    );
  }

  if (result.error) {
    return (
      <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
        <Typography color="error.dark" fontWeight="bold">
          Error:
        </Typography>
        <Typography color="error.dark" component="pre" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.error}
        </Typography>
      </Paper>
    );
  }

  const hasRows = result.rows && result.rows.length > 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with stats and export buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {result.affectedRows !== undefined ? (
            <>
              {result.affectedRows} row(s) affected
            </>
          ) : (
            <>
              {result.rows?.length || 0} row(s) returned
              {result.hasMore && ' (showing first page)'}
            </>
          )}
          {' · '}
          Execution time: {result.executionTime}ms
        </Typography>
        {hasRows && (
          <Box sx={{ ml: 'auto !important' }}>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              sx={{ mr: 1 }}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportJson}
            >
              Export JSON
            </Button>
          </Box>
        )}
      </Stack>

      {/* Grid */}
      {hasRows ? (
        <Box 
          className="ag-theme-alpine" 
          sx={{ 
            flexGrow: 1, 
            width: '100%',
            ...(isDarkMode && {
              backgroundColor: '#1e1e1e !important',
              color: '#ffffff !important',
              '& .ag-root-wrapper': {
                backgroundColor: '#1e1e1e !important',
              },
              '& .ag-header': {
                backgroundColor: '#252526 !important',
                color: '#ffffff !important',
                borderBottomColor: '#3e3e42 !important',
              },
              '& .ag-header-cell': {
                color: '#ffffff !important',
                backgroundColor: '#252526 !important',
              },
              '& .ag-header-cell-text': {
                color: '#ffffff !important',
              },
              '& .ag-row': {
                backgroundColor: '#1e1e1e !important',
                color: '#ffffff !important',
                borderBottomColor: '#3e3e42 !important',
              },
              '& .ag-row:hover': {
                backgroundColor: '#2a2d2e !important',
              },
              '& .ag-row-even': {
                backgroundColor: '#1e1e1e !important',
              },
              '& .ag-row-odd': {
                backgroundColor: '#252526 !important',
              },
              '& .ag-cell': {
                color: '#ffffff !important',
                borderColor: '#3e3e42 !important',
                backgroundColor: 'transparent !important',
              },
              '& .ag-paging-panel': {
                backgroundColor: '#252526 !important',
                color: '#ffffff !important',
                borderTopColor: '#3e3e42 !important',
              },
              '& .ag-paging-button': {
                color: '#ffffff !important',
              },
              '& .ag-status-bar': {
                backgroundColor: '#252526 !important',
                color: '#ffffff !important',
              },
              '& input, & select': {
                backgroundColor: '#252526 !important',
                color: '#ffffff !important',
                borderColor: '#3e3e42 !important',
              },
            }),
          }}
        >
          <AgGridReact
            rowData={result.rows}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            pagination={true}
            paginationPageSize={100}
            onCellDoubleClicked={onCellDoubleClicked}
            domLayout="normal"
          />
        </Box>
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Query executed successfully (no rows returned)
          </Typography>
        </Box>
      )}

      {/* Cell popup */}
      <CellPopup
        open={popupOpen}
        data={cellPopupData}
        onClose={handleCloseCellPopup}
      />
    </Box>
  );
};

export default ResultsGrid;

